import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ChartColumn, Database, Megaphone, MessageCircleQuestionMark, SendHorizontal, Trophy, Wallet } from "lucide-react";
import type { AssistantMessageDto } from "@digisubs/shared";
import { useAuth, useCan } from "@/app/auth";
import { ApiError, errorMessage } from "@/lib/api";
import { cn } from "@/lib/cn";
import { timeOnly } from "@/lib/format";
import { scrollToEnd } from "@/lib/scroll";
import { InlineAlert, Skeleton, useToast } from "@/components/ui";
import { useAiStatus, useAssistantThread, useSendAssistantMessage } from "./api";
import { AiAvatar, AiMarkdown, NoAccess } from "./parts";

// Assistant conversation used by the top bar drawer and the dashboard card: greeting with suggestions (screenshot 1),
// messages with the data sources the assistant consulted, quota and availability states, optimistic sending.

export interface Suggestion {
  label: string;
  prompt: string | null;
  icon: ReactNode;
}

export const SUGGESTIONS: Suggestion[] = [
  {
    label: "Créer une nouvelle campagne",
    prompt: "Aide-moi à préparer une nouvelle campagne publicitaire : quel produit mettre en avant, quel budget de départ et quelle audience, à partir de mes ventes et de mes campagnes actuelles.",
    icon: <Megaphone />,
  },
  { label: "Analyser vos performances", prompt: "Analyse mes performances des 7 derniers jours : ventes, commandes, livraisons et publicité, avec les points d'attention.", icon: <ChartColumn /> },
  { label: "Trouver des produits gagnants", prompt: "Quels sont mes produits gagnants sur les 30 derniers jours, et lesquels risquent une rupture de stock ?", icon: <Trophy /> },
  { label: "Optimiser votre budget", prompt: "Comment optimiser mon budget publicitaire selon le ROAS et le coût par achat de mes campagnes des 7 derniers jours ?", icon: <Wallet /> },
  { label: "Répondre à vos questions", prompt: null, icon: <MessageCircleQuestionMark /> },
];

function firstName(name: string | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

function Bubble({ message }: { message: AssistantMessageDto }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-[13.5px] leading-relaxed text-white shadow-[0_6px_18px_-10px_rgb(47_107_255/0.9)]">
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
          <p className="mt-1 text-right text-[10.5px] text-white/70">{timeOnly(message.createdAt)}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <AiAvatar size="sm" className="mt-0.5" />
      <div className="min-w-0 max-w-[88%] rounded-2xl rounded-tl-md border border-line bg-panel-2 px-3.5 py-2.5 text-ink-2">
        <AiMarkdown text={message.content} />
        {message.toolsUsed.length > 0 ? (
          <p className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-line pt-2 text-[11px] text-muted">
            <Database className="size-3" aria-hidden />
            Données consultées : {message.toolsUsed.join(", ")}
          </p>
        ) : null}
        <p className="mt-1 text-[10.5px] text-faint">{timeOnly(message.createdAt)}</p>
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex items-center gap-2.5" aria-live="polite">
      <AiAvatar size="sm" />
      <div className="flex items-center gap-1 rounded-2xl rounded-tl-md border border-line bg-panel-2 px-3.5 py-3">
        {[0, 1, 2].map((i) => (
          <span key={i} className="size-1.5 animate-pulse rounded-full bg-primary-400" style={{ animationDelay: `${i * 160}ms` }} />
        ))}
        <span className="sr-only">L'assistant analyse vos données</span>
      </div>
    </div>
  );
}

export function AssistantChat({
  threadId,
  onThreadChange,
  variant,
  className,
}: {
  threadId: string | null;
  onThreadChange: (id: string | null) => void;
  variant: "drawer" | "panel";
  className?: string;
}) {
  const canUse = useCan("ai.use");
  const { me } = useAuth();
  const toast = useToast();
  const status = useAiStatus(canUse);
  const thread = useAssistantThread(canUse ? threadId : null);
  const send = useSendAssistantMessage();
  const [text, setText] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const messages = threadId ? (thread.data?.messages ?? []) : [];

  // Scroll the messages pane only (scrollIntoView would also move the page on phones).
  useEffect(() => {
    if (messages.length > 0 || pending) scrollToEnd(listRef.current);
  }, [messages.length, pending]);

  if (!canUse) {
    return <NoAccess compact description="Votre rôle ne donne pas accès à l'assistant IA. Demandez à un administrateur de l'organisation." />;
  }

  const availability = status.data?.features.assistant;
  const unavailable = availability && !availability.available ? availability.reason : null;
  const busy = send.isPending;

  const submit = async (value: string) => {
    const message = value.trim();
    if (!message || busy) return;
    setPending(message);
    setText("");
    setQuotaError(null);
    try {
      const reply = await send.mutateAsync({ threadId, message });
      if (reply.thread.id !== threadId) onThreadChange(reply.thread.id);
    } catch (err) {
      setText(message);
      if (err instanceof ApiError && err.code === "quota_exceeded") setQuotaError(err.message);
      else toast.error("Réponse impossible", errorMessage(err));
    } finally {
      setPending(null);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit(text);
    }
  };

  const showWelcome = messages.length === 0 && !pending && !(threadId && thread.isLoading);
  const name = firstName(me?.user.name);

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div ref={listRef} className={cn("min-h-0 flex-1 overflow-y-auto", variant === "drawer" ? "px-4 py-4 sm:px-5" : "pr-1")}>
        {threadId && thread.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="ml-auto h-10 w-2/3" />
            <Skeleton className="h-24 w-5/6" />
          </div>
        ) : null}
        {threadId && thread.error ? <InlineAlert tone="error" title="Discussion introuvable">{errorMessage(thread.error)}</InlineAlert> : null}
        {showWelcome ? (
          <div className="flex items-start gap-3">
            <AiAvatar size={variant === "panel" ? "sm" : "md"} />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold text-ink">Bonjour {name} !</p>
              <p className="mt-0.5 text-[12.5px] text-muted">Je peux vous aider avec :</p>
              <ul className={cn("mt-2", variant === "panel" ? "space-y-1" : "space-y-1.5")}>
                {SUGGESTIONS.map((s) => (
                  <li key={s.label}>
                    <button
                      type="button"
                      disabled={Boolean(unavailable) || busy}
                      onClick={() => (s.prompt ? void submit(s.prompt) : inputRef.current?.focus())}
                      className={cn(
                        "group flex w-full items-center gap-2.5 rounded-lg text-left text-ink-2 transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-60",
                        variant === "panel" ? "px-1 py-1 text-[12.5px] hover:bg-panel-2" : "border border-line bg-panel-2/50 px-3 py-2.5 text-[13px] hover:border-line-strong hover:bg-panel-2",
                      )}
                    >
                      <span className="flex shrink-0 text-primary-400 [&>svg]:size-3.5">{s.icon}</span>
                      <span className="min-w-0 truncate">{s.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        {messages.length > 0 || pending ? (
          <div className="space-y-3.5">
            {messages.map((m, i) => (
              <Bubble key={`${m.createdAt}-${i}`} message={m} />
            ))}
            {pending ? (
              <>
                <Bubble message={{ role: "user", content: pending, toolsUsed: [], createdAt: new Date().toISOString() }} />
                <Typing />
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={cn("shrink-0 space-y-2", variant === "drawer" ? "border-t border-line px-4 py-3 sm:px-5" : "pt-3")}>
        {unavailable ? (
          <InlineAlert tone="warning" title="Assistant indisponible">
            {unavailable}
          </InlineAlert>
        ) : null}
        {quotaError ? (
          <InlineAlert tone="error" title="Quota atteint">
            {quotaError}
          </InlineAlert>
        ) : null}
        <div className="flex items-end gap-2">
          <label className="sr-only" htmlFor={`assistant-input-${variant}`}>
            Votre question
          </label>
          <textarea
            id={`assistant-input-${variant}`}
            ref={inputRef}
            value={text}
            rows={1}
            maxLength={4000}
            disabled={Boolean(unavailable)}
            onChange={(e) => {
              setText(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={onKeyDown}
            placeholder="Posez votre question..."
            className="min-h-10 flex-1 resize-none rounded-[10px] border border-line-strong bg-panel-2 px-3 py-2.5 text-[13.5px] leading-snug text-ink placeholder:text-faint focus:border-primary/70 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="button"
            aria-label="Envoyer"
            title="Envoyer"
            disabled={!text.trim() || busy || Boolean(unavailable)}
            onClick={() => void submit(text)}
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-primary text-white shadow-[0_6px_18px_-8px_rgb(47_107_255/0.9)] transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <SendHorizontal className="size-4.5" />
          </button>
        </div>
        {variant === "drawer" ? <p className="text-[11px] text-faint">L'assistant analyse vos données et recommande ; il n'exécute aucune action à votre place.</p> : null}
      </div>
    </div>
  );
}
